/**
 * Admin org management store for the SMS Marketing SaaS app.
 *
 * Super-admins (Animas staff) can switch between client orgs. The selected
 * org_id is persisted in localStorage so a page refresh keeps the selection.
 * When a non-null selectedOrgId is present, the API client injects X-Tenant-Id
 * on every request, overriding the JWT/hostname-resolved org on the backend.
 *
 * Import cycle resolution
 * -----------------------
 * OrgSummary lives in types/admin.ts (no deps). client.ts exposes
 * registerOrgIdProvider() instead of importing the store. This file calls
 * registerOrgIdProvider() once the store is created, wiring the getter
 * without creating a cycle.
 *
 * NOTE (L3): the org-switcher reads `/api/sms/admin/organizations` and
 * lib/tenant reads `/api/sms/tenant-context`. These two shared-infra endpoints
 * mirror the email-api equivalents (`/api/admin/organizations`,
 * `/api/tenant-context`) and are a small required addition to the L1 sms-api —
 * documented in the L3 handoff. Until they exist the switcher hides (403/404
 * is treated as "not a super-admin").
 *
 * @module lib/admin
 */

import { apiClient } from "@plerity/dashboard-shared/hooks";
import { createAppStore } from "@plerity/dashboard-shared/stores";
import { persist } from "zustand/middleware";
import { client, registerOrgIdProvider } from "../api/client";
import { asResponseGuard, ItemsEnvelopeGuard } from "../api/schemas";
import type { OrgSummary } from "../types/admin";

export type { OrgSummary };

interface AdminState {
  /** Admin override org_id -- null means use JWT/hostname-resolved org */
  selectedOrgId: number | null;
  /** Available orgs loaded from the admin API */
  organizations: OrgSummary[];
  /** Whether the current session belongs to a super-admin */
  isSuperAdmin: boolean;
  loading: boolean;
  error: string | null;

  setSelectedOrgId: (orgId: number | null) => void;
  fetchOrganizations: () => Promise<void>;
  setIsSuperAdmin: (val: boolean) => void;
  reset: () => void;
}

export const useAdminStore = createAppStore<AdminState>(
  "sms-admin",
  persist(
    (set) => ({
      selectedOrgId: null,
      organizations: [],
      isSuperAdmin: false,
      loading: false,
      error: null,

      setSelectedOrgId: (orgId) => set({ selectedOrgId: orgId }),

      fetchOrganizations: async () => {
        set({ loading: true, error: null });
        try {
          const res = await client.get<{
            success: boolean;
            data: { items: OrgSummary[]; total: number };
          }>("/api/sms/admin/organizations");
          const data = asResponseGuard<{ items: OrgSummary[]; total: number }>(ItemsEnvelopeGuard).parse(res.data.data);
          set({ organizations: data.items ?? [], loading: false });
        } catch (err) {
          const message = err instanceof Error ? err.message : "Failed to load organizations";
          set({ loading: false, error: message });
          // Re-throw so TenantContextLoader can detect non-super-admin (403)
          // and set isSuperAdmin=false correctly.
          throw err;
        }
      },

      setIsSuperAdmin: (val) => set({ isSuperAdmin: val }),

      reset: () =>
        set({
          selectedOrgId: null,
          organizations: [],
          isSuperAdmin: false,
          loading: false,
          error: null,
        }),
    }),
    {
      name: "sms-admin-org",
      /** Persist org selection + super-admin flag so auth expiry doesn't hide the dropdown */
      partialize: (state) => ({
        selectedOrgId: state.selectedOrgId,
        isSuperAdmin: state.isSuperAdmin,
      }),
    }
  )
);

/**
 * Wire the X-Tenant-Id provider into the local API client.
 */
registerOrgIdProvider(() => useAdminStore.getState().selectedOrgId);

/**
 * Wire the X-Tenant-Id provider into the SHARED dashboard `apiClient`.
 *
 * The dashboard-shared hooks (useApiQuery, usePaginatedQuery) use their own
 * internal `apiClient`, distinct from this app's local `client`. Registering the
 * same X-Tenant-Id interceptor here makes ALL requests (queries AND mutations)
 * carry X-Tenant-Id when a super-admin has selected a specific org. `main.tsx`
 * invokes this once at app startup, before the first shared-hook query fires.
 */
export function registerSharedApiClientOrgInterceptor(): void {
  apiClient.interceptors.request.use((config) => {
    const selectedOrgId = useAdminStore.getState().selectedOrgId;
    if (selectedOrgId !== null) {
      config.headers.set("X-Tenant-Id", String(selectedOrgId));
    }
    return config;
  });
}
